import { ChangeEvent, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import {
    getPrizeDrawParticipations,
    getLotteryParticipations,
    getInstantWinWinnersParticipations,
} from "../../../graphql/queries";
import { Box, Typography, Autocomplete, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { ConfigType } from "../../../types/configuration";
import { TooltipText } from "../../../components/inputComponents/TooltipText";
import { Notification } from "../../../common/Notification/Notification";
import TextFieldWrapper from "../../../common/TextFieldWrapper/TextFieldWrapper";
import { notificationType } from "../../../types/notifications";
import { ToolTipPlacement } from "../../../types/componentTypes/tooltip";
import type {
    GenerateOption,
    DataToExport,
    LotteryParticipationExportInput,
    ParticipationExportRes,
    LotteryParticipationExportRes,
    IwParticipationExportInput,
    PdParticipationExportInput,
} from "../../../types/componentTypes/ParticipationExportTypes";
import { defaultNotifState } from "../../../constants/currency-constants";
import "./ParticipationExport.css";
import WinnersTable from "./WinnersTable";
import ExportStatusMessage from "./ExportStatusMessage";
import { getMechanicFlow } from "../../../utils/configUtils";
import moment from "moment";
import CustomWinnersExport from "./CustomWinnersExport";

const options = {
    instantWin: [
        { label: "Full Winners Export", value: "default" },
        { label: "Custom Winners Export (select dates)", value: "custom" },
    ],
    promoEntry: [
        { label: "Full Export", value: "default" },
        { label: "Custom Export (number of winners)", value: "custom" },
    ],
};

const pdTooltipTxt =
    "You can choose between full export or custom export. The full export consists of all participations till the moment of the download. The custom export has only randomly selected winners based on the value populated in the number of winners field.";

const iwTooltipTxt =
    "You can choose between full winners export and custom winners export. The full winners export consists of all winning participations till the moment of download. The custom winners export gives you the option to get all the winning participations between specified dates.";

const defaultNumberOfWinners = 1;

const ParticipationExport = ({ config }: { config: ConfigType }) => {
    const mechanic = getMechanicFlow(config) as "instantWin" | "promoEntry";
    const [selectedGenerateOption, setSelectedGenerateOption] = useState<GenerateOption>("default");
    const [numberOfWinners, setNumberOfWinners] = useState<number>(defaultNumberOfWinners);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [dataToExport, setDataToExport] = useState<DataToExport>(null);
    const [csvMessage, setCSVMessage] = useState<boolean>(false);
    const [notificationState, setNotificationState] = useState<notificationType>(defaultNotifState);
    const [isBatchForWinners, setIsBatchForWinners] = useState<boolean>(false);
    const [iwCustomDates, setIwCustomDates] = useState<{ startDate: number; endDate: number }>({
        startDate: config.configurationParameters.configurationStartUtc,
        endDate: moment().valueOf(),
    });

    const handleOptionSelected = (_, option: { label: string; value: string }) => {
        setSelectedGenerateOption(option?.value as GenerateOption);
        setIsGenerating(false);
        setDataToExport(null);
    };

    const fetchAndParse = async <TInput extends object, TResponse extends object>(
        query: string,
        inputName: string,
        input: TInput
    ): Promise<TResponse> => {
        const res = (await API.graphql(
            graphqlOperation(query, {
                [inputName]: input,
            })
        )) as GraphQLResult<{ body: string }>;
        const dataToParse = res.data?.[Object.keys(res.data)[0]]?.body;
        return JSON.parse(dataToParse);
    };

    const getCsvData = {
        instantWin: {
            default: () =>
                fetchAndParse<IwParticipationExportInput, ParticipationExportRes>(
                    getInstantWinWinnersParticipations,
                    "instantWinExporterLambdaInput",
                    {
                        configurationId: config.configurationId,
                        startDate: config.configurationParameters.configurationStartUtc,
                        endDate: moment.utc().valueOf(),
                    }
                ),
            custom: () =>
                fetchAndParse<IwParticipationExportInput, ParticipationExportRes>(
                    getInstantWinWinnersParticipations,
                    "instantWinExporterLambdaInput",
                    {
                        configurationId: config.configurationId,
                        startDate: moment.utc(iwCustomDates.startDate).valueOf(),
                        endDate: moment.utc(iwCustomDates.endDate).valueOf(),
                    }
                ),
        },
        promoEntry: {
            default: () =>
                fetchAndParse<PdParticipationExportInput, ParticipationExportRes>(
                    getPrizeDrawParticipations,
                    "prizeDrawLambdaInput",
                    {
                        configurationId: config.configurationId,
                    }
                ),
            custom: () =>
                fetchAndParse<LotteryParticipationExportInput, LotteryParticipationExportRes>(
                    getLotteryParticipations,
                    "lotteryLambdaInput",
                    {
                        configurationId: config.configurationId,
                        numberOfWinners: numberOfWinners,
                    }
                ),
        },
    };

    const handleGenerate = async () => {
        setIsGenerating(true);
        setIsBatchForWinners(false);
        try {
            const res = (await getCsvData[mechanic][selectedGenerateOption]()) as
                | ParticipationExportRes
                | LotteryParticipationExportRes;
            if (res?.customErrorMessage) {
                throw new Error(res.customErrorMessage);
            }
            if (res?.message) setCSVMessage(true);
            if ((res as LotteryParticipationExportRes)?.isBatch === "true") {
                setIsBatchForWinners(true);
            }
            setDataToExport(res);
        } catch (err) {
            setNotificationState({
                open: true,
                title: "Generating participations export failed",
                content: (err as Error).message,
                level: "error",
            });
            console.error("Generating export failed with: ", err);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleNumberOfWinnerOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setDataToExport(null);
        setNumberOfWinners(parseInt(e.target.value) || 0);
        setIsGenerating(false);
    };

    const copyToClipboard = (value: string) => {
        navigator.clipboard.writeText(value);
        setNotificationState({
            open: true,
            title: "Participation CSV Link copied!",
            content: "",
            level: "success",
        });
    };

    return (
        <>
            <Notification notificationState={notificationState} setNotificationState={setNotificationState} />
            <Box className='pe-heading'>
                <Typography variant='h3'>Participation Export</Typography>
            </Box>
            <Box className='pe-main-box'>
                <Box className='pe-selector'>
                    <TooltipText
                        text={"Select export type"}
                        textVariant='body1'
                        iconSize='medium'
                        styleName='tooltip'
                        tooltip={mechanic === "instantWin" ? iwTooltipTxt : pdTooltipTxt}
                        placement={ToolTipPlacement.RightEnd}
                    />
                    <Autocomplete
                        defaultValue={options[mechanic][0]}
                        options={options[mechanic] as any}
                        getOptionLabel={(option) => option.label}
                        getOptionDisabled={(option) => option.value === selectedGenerateOption}
                        renderInput={(params) => <TextFieldWrapper {...params} />}
                        onChange={handleOptionSelected}
                    />
                    {selectedGenerateOption === "custom" && (
                        <CustomWinnersExport
                            dataToExport={dataToExport}
                            mechanic={mechanic}
                            copyToClipboard={copyToClipboard}
                            isBatchForWinners={isBatchForWinners}
                            numberOfWinners={numberOfWinners}
                            handleNumberOfWinnerOnChange={handleNumberOfWinnerOnChange}
                            iwCustomDates={iwCustomDates}
                            setIwCustomDates={setIwCustomDates}
                            setDataToExport={setDataToExport}
                            config={config}
                        />
                    )}
                    <Box className='pe-button-box'>
                        {isGenerating && (
                            <Typography variant='body1' className='pe-message'>
                                Please wait, file is being processed. It might take some time.
                            </Typography>
                        )}
                        {!dataToExport && (
                            <Button
                                variant='contained'
                                color='primary'
                                onClick={handleGenerate}
                                disabled={isGenerating || numberOfWinners < 1}>
                                Generate Export
                            </Button>
                        )}
                        {dataToExport && !csvMessage && !isBatchForWinners && (
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<DownloadIcon />}
                                className='pe-button'
                                onClick={() => window.open(dataToExport.csv, "_blank")}>
                                Download
                            </Button>
                        )}
                        {dataToExport && csvMessage && selectedGenerateOption === "default" && (
                            <ExportStatusMessage
                                message='Due to the large number of participants, the file is still in progress. Please check the link below again in 15 minutes.'
                                csvUrl={dataToExport.csv}
                                onCopy={() => copyToClipboard(dataToExport.csv)}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
            {dataToExport && !isBatchForWinners && selectedGenerateOption === "custom" && mechanic !== "instantWin" && (
                <WinnersTable data-testid='winners-table' data={dataToExport as LotteryParticipationExportRes} />
            )}
        </>
    );
};

export { ParticipationExport };
